/**
 * SIP Trunk List Page - Table view of SIP trunks with URL-driven filters, ordering, and pagination.
 */
import { useEffect, useState, useCallback, useMemo, type ChangeEvent } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import DialerSipIcon from '@mui/icons-material/DialerSip';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Public,
  Sync,
  CheckCircle,
  Cancel,
  Clear,
  CallReceived,
  Hub,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { RequirePermission } from '../contexts/PermissionContext';
import api from '../services/api';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { useListSearchParams } from '../hooks/useListSearchParams';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';
import FilteredListLayout from '../components/FilteredListLayout';
import ListActionButton from '../components/ListActionButton';
import usePageTitle from '../hooks/usePageTitle';
import ButtonTabs, { type ButtonTab } from '../components/ButtonTabs';
import { useAuth } from '../contexts/AuthContext';

interface SipTrunk {
  id: string;
  name: string;
  host: string;
  port: number;
  transport: string;
  tenantName: string | null;
  isGlobal: boolean;
  isActive: boolean;
  pools?: SipTrunkMembership[];
  outboundRoutes?: SipTrunkOutboundRouteOption[];
}

interface SipTrunkMembership {
  id: string;
  name: string;
  isActive: boolean;
  isGlobal: boolean;
}

interface SipTrunkPoolMember {
  id?: string;
  sipTrunkOutboundRouteId: string;
  sipTrunkOutboundRouteName?: string;
  sipTrunkOutboundRouteIsActive?: boolean;
  sipTrunkId?: string;
  sipTrunkName?: string;
  sipTrunkHost?: string;
  sipTrunkPort?: number;
  sipTrunkIsActive?: boolean;
  isActive: boolean;
  baseWeight: number;
  maxConcurrentCalls?: number | null;
  priority: number;
  notes?: string;
}

interface SipTrunkPool {
  id: string;
  name: string;
  description?: string;
  tenantName: string | null;
  isGlobal: boolean;
  isActive: boolean;
  routingStrategy: string;
  maxConcurrentCalls?: number | null;
  members: SipTrunkPoolMember[];
}

interface SipTrunkOption {
  id: string;
  name: string;
  outboundRoutes?: SipTrunkOutboundRouteOption[];
}

interface SipTrunkOutboundRouteOption {
  id: string;
  name: string;
  sipTrunkId?: string;
  sipTrunkName?: string;
  host?: string;
  port?: number;
  isActive?: boolean;
  priority?: number;
}

interface SipTrunkInboundRoute {
  id: string;
  sipTrunkId: string;
  sipTrunkName: string;
  name: string;
  isActive: boolean;
  aiAgentName: string;
  phoneNumberRaw: string;
  normalizedPhoneNumber: string;
  transferTargetTypeDisplay?: string;
  appointmentCalendarName?: string;
  isMaterializable?: boolean;
}

const DEFAULT_ORDERING = '-created_at';
const SIP_TRUNK_TAB_TRUNKS = 'trunks';
const SIP_TRUNK_TAB_POOLS = 'pools';
const SIP_TRUNK_TAB_INBOUND_ROUTES = 'inbound-routes';

const createEmptyPoolForm = () => ({
  name: '',
  description: '',
  isActive: true,
  isGlobal: false,
  maxConcurrentCalls: '',
  members: [] as SipTrunkPoolMember[],
});

export default function SipTrunkListPage() {
  const { t } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:sipTrunks.title'));
  const { isSuperuser } = useAuth();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = tabParam === SIP_TRUNK_TAB_POOLS
    ? SIP_TRUNK_TAB_POOLS
    : tabParam === SIP_TRUNK_TAB_INBOUND_ROUTES
      ? SIP_TRUNK_TAB_INBOUND_ROUTES
      : SIP_TRUNK_TAB_TRUNKS;
  const [sipTrunks, setSipTrunks] = useState<SipTrunk[]>([]);
  const [sipTrunkPools, setSipTrunkPools] = useState<SipTrunkPool[]>([]);
  const [sipTrunkInboundRoutes, setSipTrunkInboundRoutes] = useState<SipTrunkInboundRoute[]>([]);
  const [sipTrunkOptions, setSipTrunkOptions] = useState<SipTrunkOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [poolDialogOpen, setPoolDialogOpen] = useState(false);
  const [editingPool, setEditingPool] = useState<SipTrunkPool | null>(null);
  const [poolForm, setPoolForm] = useState(createEmptyPoolForm);
  const [savingPool, setSavingPool] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalSipTrunks, setTotalSipTrunks] = useState(0);
  const [totalSipTrunkPools, setTotalSipTrunkPools] = useState(0);
  const [totalSipTrunkInboundRoutes, setTotalSipTrunkInboundRoutes] = useState(0);

  const { confirm, DialogComponent } = useConfirmDialog();

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['status', 'search'],
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: DEFAULT_ORDERING,
  });

  const statusFilterValue = filters.status ?? '';
  const searchFilterValue = filters.search ?? '';
  const orderingParam = ordering || DEFAULT_ORDERING;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const flatButtonSx = {
    textTransform: 'none',
    boxShadow: 'none',
  };
  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiSelect-select': {  
      padding: '11px 12px',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
  };

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: t('common:status.active') },
      { value: 'inactive', label: t('common:status.inactive') },
    ],
    [t],
  );

  const orderingOptions = useMemo(
    () => [
      { value: '-created_at', label: t('pages:sipTrunks.filters.orderingOptions.createdAtDesc') },
      { value: 'created_at', label: t('pages:sipTrunks.filters.orderingOptions.createdAtAsc') },
      { value: 'name', label: t('pages:sipTrunks.filters.orderingOptions.nameAsc') },
      { value: '-name', label: t('pages:sipTrunks.filters.orderingOptions.nameDesc') },
      { value: 'status', label: t('pages:sipTrunks.filters.orderingOptions.status') },
    ],
    [t],
  );

  const sipOutboundRouteOptions = useMemo(
    () => sipTrunkOptions.flatMap((trunk) => (trunk.outboundRoutes || []).map((route) => ({
      ...route,
      sipTrunkId: route.sipTrunkId || trunk.id,
      sipTrunkName: route.sipTrunkName || trunk.name,
    }))),
    [sipTrunkOptions],
  );

  const formatOutboundRouteOption = useCallback((route: SipTrunkOutboundRouteOption) => {
    const trunkLabel = route.sipTrunkName || route.sipTrunkId || t('pages:sipTrunks.pools.fields.unknownTrunk');
    const routeLabel = route.name || t('pages:sipTrunks.pools.fields.defaultOutboundRoute');
    const hostLabel = route.host && route.port ? `${route.host}:${route.port}` : route.host;
    return [trunkLabel, routeLabel, hostLabel].filter(Boolean).join(' · ');
  }, [t]);

  const hasActiveFilters =
    Boolean(searchFilterValue) || Boolean(statusFilterValue) || orderingParam !== DEFAULT_ORDERING;

  const fetchSipTrunks = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);
      const params: Record<string, number | string | boolean> = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: orderingParam,
      };

      if (statusFilterValue === 'active') {
        params.is_active = true;
      } else if (statusFilterValue === 'inactive') {
        params.is_active = false;
      }

      if (searchFilterValue) {
        params.search = searchFilterValue;
      }

      const response = await api.get('/api/sip-trunks/', {
        params,
      });

      const data = response.data.results ?? response.data;
      setSipTrunks(Array.isArray(data) ? data : []);
      setTotalSipTrunks(response.data.count ?? data.length ?? 0);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      console.error('Error loading SIP trunks:', err);
      setError(error?.response?.data?.detail || t('pages:sipTrunks.errors.loading'));
      setSipTrunks([]);
      setTotalSipTrunks(0);
    } finally {
      setLoading(false);
    }
  }, [orderingParam, page, rowsPerPage, searchFilterValue, statusFilterValue, t]);

  const fetchSipTrunkPools = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);
      const params: Record<string, number | string | boolean> = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: orderingParam,
      };

      if (statusFilterValue === 'active') {
        params.is_active = true;
      } else if (statusFilterValue === 'inactive') {
        params.is_active = false;
      }

      if (searchFilterValue) {
        params.search = searchFilterValue;
      }

      const response = await api.get('/api/sip-trunks/pools/', { params });
      const data = response.data.results ?? response.data;
      setSipTrunkPools(Array.isArray(data) ? data : []);
      setTotalSipTrunkPools(response.data.count ?? data.length ?? 0);
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { detail?: string } } };
      console.error('Error loading SIP trunk pools:', err);
      setError(apiError?.response?.data?.detail || t('pages:sipTrunks.pools.errors.loading'));
      setSipTrunkPools([]);
      setTotalSipTrunkPools(0);
    } finally {
      setLoading(false);
    }
  }, [orderingParam, page, rowsPerPage, searchFilterValue, statusFilterValue, t]);

  const fetchSipTrunkInboundRoutes = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);
      const params: Record<string, number | string> = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: orderingParam,
      };

      if (statusFilterValue) {
        params.status = statusFilterValue;
      }

      if (searchFilterValue) {
        params.search = searchFilterValue;
      }

      const response = await api.get('/api/sip-trunks/inbound-routes/', { params });
      const data = response.data.results ?? response.data;
      setSipTrunkInboundRoutes(Array.isArray(data) ? data : []);
      setTotalSipTrunkInboundRoutes(response.data.count ?? data.length ?? 0);
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { detail?: string } } };
      console.error('Error loading inbound SIP routes:', err);
      setError(apiError?.response?.data?.detail || t('pages:sipTrunks.inbound.errors.routesLoading'));
      setSipTrunkInboundRoutes([]);
      setTotalSipTrunkInboundRoutes(0);
    } finally {
      setLoading(false);
    }
  }, [orderingParam, page, rowsPerPage, searchFilterValue, statusFilterValue, t]);

  const fetchSipTrunkOptions = useCallback(async () => {
    try {
      const response = await api.get('/api/sip-trunks/list_simple/');
      const data = response.data.results ?? response.data ?? [];
      setSipTrunkOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading SIP trunk options:', err);
      setSipTrunkOptions([]);
    }
  }, []);

  useEffect(() => {
    if (activeTab === SIP_TRUNK_TAB_POOLS) {
      fetchSipTrunkPools();
      fetchSipTrunkOptions();
      return;
    }
    if (activeTab === SIP_TRUNK_TAB_INBOUND_ROUTES) {
      fetchSipTrunkInboundRoutes();
      return;
    }
    fetchSipTrunks();
  }, [activeTab, fetchSipTrunkInboundRoutes, fetchSipTrunkOptions, fetchSipTrunkPools, fetchSipTrunks]);

  const openCreatePoolDialog = () => {
    setEditingPool(null);
    setPoolForm(createEmptyPoolForm());
    fetchSipTrunkOptions();
    setPoolDialogOpen(true);
  };

  const openEditPoolDialog = (pool: SipTrunkPool) => {
    setEditingPool(pool);
    setPoolForm({
      name: pool.name,
      description: pool.description || '',
      isActive: pool.isActive,
      isGlobal: pool.isGlobal,
      maxConcurrentCalls: pool.maxConcurrentCalls == null ? '' : String(pool.maxConcurrentCalls),
      members: pool.members.map((member) => ({
        ...member,
        maxConcurrentCalls: member.maxConcurrentCalls ?? null,
        notes: member.notes || '',
      })),
    });
    fetchSipTrunkOptions();
    setPoolDialogOpen(true);
  };

  const closePoolDialog = () => {
    if (savingPool) {
      return;
    }
    setPoolDialogOpen(false);
    setEditingPool(null);
    setPoolForm(createEmptyPoolForm());
  };

  const addPoolMember = () => {
    setPoolForm((prev) => ({
      ...prev,
      members: [
        ...prev.members,
        {
          sipTrunkOutboundRouteId: '',
          isActive: true,
          baseWeight: 1,
          maxConcurrentCalls: null,
          priority: 100,
          notes: '',
        },
      ],
    }));
  };

  const updatePoolMember = (index: number, patch: Partial<SipTrunkPoolMember>) => {
    setPoolForm((prev) => ({
      ...prev,
      members: prev.members.map((member, memberIndex) => (
        memberIndex === index ? { ...member, ...patch } : member
      )),
    }));
  };

  const removePoolMember = (index: number) => {
    setPoolForm((prev) => ({
      ...prev,
      members: prev.members.filter((_, memberIndex) => memberIndex !== index),
    }));
  };

  const savePool = async () => {
    setSavingPool(true);
    setError(null);
    try {
      const payload = {
        name: poolForm.name,
        description: poolForm.description,
        isActive: poolForm.isActive,
        isGlobal: poolForm.isGlobal,
        maxConcurrentCalls: poolForm.maxConcurrentCalls === '' ? null : Number(poolForm.maxConcurrentCalls),
        members: poolForm.members.map((member) => ({
          id: member.id,
          sipTrunkOutboundRouteId: member.sipTrunkOutboundRouteId,
          isActive: member.isActive,
          baseWeight: Number(member.baseWeight),
          maxConcurrentCalls: member.maxConcurrentCalls == null ? null : Number(member.maxConcurrentCalls),
          priority: Number(member.priority),
          notes: member.notes || '',
        })),
      };
      if (editingPool) {
        await api.patch(`/api/sip-trunks/pools/${editingPool.id}/`, payload);
      } else {
        await api.post('/api/sip-trunks/pools/', payload);
      }
      setPoolDialogOpen(false);
      setEditingPool(null);
      setPoolForm(createEmptyPoolForm());
      fetchSipTrunkPools();
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { detail?: string; name?: string[]; members?: string[] } } };
      console.error('Error saving SIP trunk pool:', err);
      setError(
        apiError?.response?.data?.detail
        || apiError?.response?.data?.name?.[0]
        || apiError?.response?.data?.members?.[0]
        || t('pages:sipTrunks.pools.errors.saving'),
      );
    } finally {
      setSavingPool(false);
    }
  };

  const tabItems = useMemo<ButtonTab[]>(
    () => [
      {
        id: SIP_TRUNK_TAB_TRUNKS,
        label: t('pages:sipTrunks.tabs.trunks'),
        icon: <DialerSipIcon />,
      },
      {
        id: SIP_TRUNK_TAB_POOLS,
        label: t('pages:sipTrunks.tabs.pools'),
        icon: <Hub />,
      },
      {
        id: SIP_TRUNK_TAB_INBOUND_ROUTES,
        label: t('pages:sipTrunks.tabs.inboundRoutes'),
        icon: <CallReceived />,
      },
    ],
    [t],
  );

  const refreshActiveTab = activeTab === SIP_TRUNK_TAB_POOLS
    ? fetchSipTrunkPools
    : activeTab === SIP_TRUNK_TAB_INBOUND_ROUTES
      ? fetchSipTrunkInboundRoutes
      : fetchSipTrunks;

  const totalRows = activeTab === SIP_TRUNK_TAB_POOLS
    ? totalSipTrunkPools
    : activeTab === SIP_TRUNK_TAB_INBOUND_ROUTES
      ? totalSipTrunkInboundRoutes
      : totalSipTrunks;

  const headerActions = (
    <>
      <ListActionButton
        variant="outlined"
        startIcon={<Refresh />}
        onClick={refreshActiveTab}
        disabled={loading}
      >
        {t('common:actions.refresh')}
      </ListActionButton>
      <RequirePermission permission="sip_trunks_manage">
        {activeTab === SIP_TRUNK_TAB_POOLS ? (
          <ListActionButton
            variant="contained"
            startIcon={<Add />}
            onClick={openCreatePoolDialog}
          >
            {t('pages:sipTrunks.pools.actions.create')}
          </ListActionButton>
        ) : activeTab === SIP_TRUNK_TAB_TRUNKS ? (
          <ListActionButton
            variant="contained"
            startIcon={<Add />}
            component={RouterLink}
            to="/sip-trunks/new"
          >
            {t('pages:sipTrunks.actions.create')}
          </ListActionButton>
        ) : null}
      </RequirePermission>
    </>
  );

  const handleDelete = async (trunk: SipTrunk) => {
    const confirmed = await confirm({
      title: t('pages:sipTrunks.delete.title'),
      message: t('pages:sipTrunks.delete.description', { name: trunk.name }),
      confirmText: t('common:actions.delete'),
      cancelText: t('common:actions.cancel'),
      type: 'error',
      icon: <Delete color="error" />,
      confirmIcon: <Delete />,
    });

    if (confirmed) {
      try {
        await api.delete(`/api/sip-trunks/${trunk.id}/`);
        fetchSipTrunks();
      } catch (err) {
        console.error('Error deleting SIP trunk:', err);
      }
    }
  };

  const handleSync = async (trunk: SipTrunk) => {
    try {
      await api.post(`/api/sip-trunks/${trunk.id}/sync/`);
      fetchSipTrunks();
    } catch (err) {
      console.error('Error syncing SIP trunk:', err);
    }
  };

  const isPoolFormValid = Boolean(
    poolForm.name.trim()
    && poolForm.members.length > 0
    && poolForm.members.every((member) => member.sipTrunkOutboundRouteId && Number(member.baseWeight) >= 0),
  );

  return (
    <RequirePermission permission="sip_trunks_access">
      <Box>
        <FilteredListLayout
          headerProps={{
            icon: <DialerSipIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
            title: t('pages:sipTrunks.title'),
            subtitle: t('pages:sipTrunks.subtitle'),
            actions: headerActions,
          }}
          filterControls={(
            <>
              <Box sx={{ flexBasis: '100%' }}>
                <ButtonTabs
                  tabs={tabItems}
                  defaultTab={SIP_TRUNK_TAB_TRUNKS}
                  tabParam="tab"
                  useUrl
                  renderContent={false}
                  sx={{ mt: -3 }}
                />
              </Box>
              <TextField
                size="small"
                label={t('common:actions.search')}
                placeholder={t('pages:sipTrunks.filters.searchPlaceholder')}
                value={searchFilterValue}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  setParams({ search: value.trim() || undefined }, { resetPage: true });
                }}
                sx={{ ...filterInputSx, minWidth: 220, flex: '1 1 220px' }}
              />
              <FormControl size="small" sx={{ minWidth: 180, ...filterInputSx }}>
                <InputLabel id="sip-trunks-status-filter-label">
                  {t('pages:sipTrunks.filters.statusLabel')}
                </InputLabel>
                <Select
                  labelId="sip-trunks-status-filter-label"
                  value={statusFilterValue}
                  label={t('pages:sipTrunks.filters.statusLabel')}
                  onChange={(event: SelectChangeEvent<string>) => {
                    const value = event.target.value;
                    setParams({ status: value || undefined }, { resetPage: true });
                  }}
                >
                  <MenuItem value="">
                    <em>{t('common:common.all')}</em>
                  </MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200, ...filterInputSx }}>
                <InputLabel id="sip-trunks-ordering-label">
                  {t('pages:sipTrunks.filters.orderingLabel')}
                </InputLabel>
                <Select
                  labelId="sip-trunks-ordering-label"
                  value={orderingParam}
                  label={t('pages:sipTrunks.filters.orderingLabel')}
                  onChange={(event: SelectChangeEvent<string>) => {
                    const value = event.target.value;
                    setParams({ ordering: value || undefined }, { resetPage: true });
                  }}
                >
                  {orderingOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Clear />}
                disabled={!hasActiveFilters}
                onClick={() => {
                  setParams({ search: null, status: null, ordering: null }, { resetPage: true });
                }}
                sx={{
                  ...flatButtonSx,
                  color: theme.palette.primary.main,
                  borderColor: alpha(theme.palette.primary.main, 0.6),
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                {t('common:actions.clear')}
              </Button>
            </>
          )}
          table={(
            activeTab === SIP_TRUNK_TAB_POOLS ? (
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      '& th': {
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.75rem',
                      },
                    }}
                  >
                    <TableCell>{t('pages:sipTrunks.pools.table.name')}</TableCell>
                    <TableCell>{t('pages:sipTrunks.pools.table.members')}</TableCell>
                    <TableCell>{t('pages:sipTrunks.pools.table.limit')}</TableCell>
                    <TableCell>{t('pages:sipTrunks.table.owner')}</TableCell>
                    <TableCell align="center">{t('pages:sipTrunks.table.status')}</TableCell>
                    <TableCell align="right">{t('common:actions.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(6)].map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton animation="wave" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : sipTrunkPools.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <Hub sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                          {t('pages:sipTrunks.pools.empty.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('pages:sipTrunks.pools.empty.description')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sipTrunkPools.map((pool) => (
                      <TableRow
                        key={pool.id}
                        hover
                        sx={{
                          transition: 'background 0.2s',
                          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography fontWeight="medium">{pool.name}</Typography>
                            {pool.description && (
                              <Typography variant="caption" color="text.secondary">
                                {pool.description}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {pool.members.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                {t('pages:sipTrunks.pools.noMembers')}
                              </Typography>
                            ) : (
                              pool.members.map((member) => (
                                <Chip
                                  key={member.id || member.sipTrunkOutboundRouteId || member.sipTrunkId}
                                  label={`${member.sipTrunkName || member.sipTrunkId} · ${member.sipTrunkOutboundRouteName || t('pages:sipTrunks.pools.fields.defaultOutboundRoute')} · ${member.baseWeight}`}
                                  size="small"
                                  color={member.isActive && member.sipTrunkIsActive && member.sipTrunkOutboundRouteIsActive !== false ? 'primary' : 'default'}
                                  variant={member.isActive && member.sipTrunkIsActive && member.sipTrunkOutboundRouteIsActive !== false ? 'filled' : 'outlined'}
                                />
                              ))
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {pool.maxConcurrentCalls ?? t('pages:sipTrunks.pools.unlimited')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {pool.isGlobal ? (
                            <Chip
                              icon={<Public sx={{ fontSize: 16 }} />}
                              label={t('pages:sipTrunks.global')}
                              size="small"
                              color="warning"
                            />
                          ) : (
                            <Typography variant="body2">{pool.tenantName}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {pool.isActive ? (
                            <Chip
                              icon={<CheckCircle sx={{ fontSize: 16 }} />}
                              label={t('common:status.active')}
                              size="small"
                              color="success"
                            />
                          ) : (
                            <Chip
                              icon={<Cancel sx={{ fontSize: 16 }} />}
                              label={t('common:status.inactive')}
                              size="small"
                              color="default"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <RequirePermission permission="sip_trunks_manage">
                            <Tooltip title={t('common:actions.edit')}>
                              <IconButton
                                size="small"
                                onClick={() => openEditPoolDialog(pool)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </RequirePermission>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : activeTab === SIP_TRUNK_TAB_INBOUND_ROUTES ? (
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      '& th': {
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.75rem',
                      },
                    }}
                  >
                    <TableCell>{t('pages:sipTrunks.inbound.table.route')}</TableCell>
                    <TableCell>{t('pages:sipTrunks.inbound.table.trunk')}</TableCell>
                    <TableCell>{t('pages:sipTrunks.inbound.table.did')}</TableCell>
                    <TableCell>{t('pages:sipTrunks.inbound.table.agent')}</TableCell>
                    <TableCell>{t('pages:sipTrunks.inbound.table.target')}</TableCell>
                    <TableCell align="center">{t('pages:sipTrunks.table.status')}</TableCell>
                    <TableCell align="right">{t('common:actions.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(7)].map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton animation="wave" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : sipTrunkInboundRoutes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <CallReceived sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                          {t('pages:sipTrunks.inbound.routes.emptyTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('pages:sipTrunks.inbound.routes.emptyListDescription')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sipTrunkInboundRoutes.map((route) => (
                      <TableRow
                        key={route.id}
                        hover
                        sx={{
                          transition: 'background 0.2s',
                          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                        }}
                      >
                        <LinkTableCell to={`/sip-trunks/${route.sipTrunkId}?tab=inbound`}>
                          <Typography fontWeight="medium">{route.name}</Typography>
                        </LinkTableCell>
                        <LinkTableCell to={`/sip-trunks/${route.sipTrunkId}?tab=inbound`}>
                          <Typography variant="body2">{route.sipTrunkName}</Typography>
                        </LinkTableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {route.phoneNumberRaw}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {route.normalizedPhoneNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{route.aiAgentName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {route.transferTargetTypeDisplay && (
                              <Chip label={route.transferTargetTypeDisplay} size="small" />
                            )}
                            {route.appointmentCalendarName && (
                              <Chip label={route.appointmentCalendarName} size="small" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={route.isActive && route.isMaterializable ? <CheckCircle sx={{ fontSize: 16 }} /> : undefined}
                            label={route.isActive ? t('common:status.active') : t('common:status.inactive')}
                            size="small"
                            color={route.isActive && route.isMaterializable ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <RequirePermission permission="sip_trunks_manage">
                            <Tooltip title={t('common:actions.edit')}>
                              <IconButton
                                size="small"
                                component={RouterLink}
                                to={`/sip-trunks/${route.sipTrunkId}?tab=inbound`}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </RequirePermission>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : (
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '& th': {
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontSize: '0.75rem',
                    },
                  }}
                >
                  <TableCell>{t('pages:sipTrunks.table.name')}</TableCell>
                  <TableCell>{t('pages:sipTrunks.table.host')}</TableCell>
                  <TableCell>{t('pages:sipTrunks.table.transport')}</TableCell>
                  <TableCell>{t('pages:sipTrunks.table.owner')}</TableCell>
                  <TableCell>{t('pages:sipTrunks.table.pools')}</TableCell>
                  <TableCell align="center">{t('pages:sipTrunks.table.status')}</TableCell>
                <TableCell align="right">{t('common:actions.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(7)].map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton animation="wave" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : sipTrunks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <DialerSipIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        {t('pages:sipTrunks.empty.title')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:sipTrunks.empty.description')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sipTrunks.map((trunk) => (
                    <TableRow
                      key={trunk.id}
                      hover
                      sx={{
                        transition: 'background 0.2s',
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                      }}
                    >
                      <LinkTableCell to={`/sip-trunks/${trunk.id}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DialerSipIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          <Typography fontWeight="medium">{trunk.name}</Typography>
                        </Box>
                      </LinkTableCell>
                      <LinkTableCell to={`/sip-trunks/${trunk.id}`}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {trunk.host}:{trunk.port}
                        </Typography>
                      </LinkTableCell>
                      <TableCell>
                        <Chip
                          label={trunk.transport.toUpperCase()}
                          size="small"
                          color={trunk.transport === 'tls' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {trunk.isGlobal ? (
                          <Chip
                            icon={<Public sx={{ fontSize: 16 }} />}
                            label={t('pages:sipTrunks.global')}
                            size="small"
                            color="warning"
                          />
                        ) : (
                          <Typography variant="body2">{trunk.tenantName}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(trunk.pools || []).length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              {t('pages:sipTrunks.pools.none')}
                            </Typography>
                          ) : (
                            (trunk.pools || []).map((pool) => (
                              <Chip
                                key={pool.id}
                                label={pool.name}
                                size="small"
                                variant={pool.isActive ? 'filled' : 'outlined'}
                                color={pool.isActive ? 'primary' : 'default'}
                              />
                            ))
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {trunk.isActive ? (
                          <Chip
                            icon={<CheckCircle sx={{ fontSize: 16 }} />}
                            label={t('common:status.active')}
                            size="small"
                            color="success"
                          />
                        ) : (
                          <Chip
                            icon={<Cancel sx={{ fontSize: 16 }} />}
                            label={t('common:status.inactive')}
                            size="small"
                            color="default"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('pages:sipTrunks.actions.sync')}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleSync(trunk)}
                              disabled={!trunk.isActive}
                            >
                              <Sync fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <RequirePermission permission="sip_trunks_manage">
                          <Tooltip title={t('common:actions.edit')}>
                            <IconButton
                              size="small"
                              component={RouterLink}
                              to={`/sip-trunks/${trunk.id}`}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </RequirePermission>
                        <RequirePermission permission="sip_trunks_manage">
                          <Tooltip title={t('common:actions.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(trunk)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </RequirePermission>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            )
          )}
          tableFooter={
            totalRows > 0 ? (
              <LinkPagination
                component="div"
                count={totalRows}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage={t('common:pagination.rowsPerPage')}
                getItemAriaLabel={(type) => {
                  if (type === 'first') return t('common:pagination.firstPage');
                  if (type === 'last') return t('common:pagination.lastPage');
                  if (type === 'next') return t('common:pagination.nextPage');
                  return t('common:pagination.previousPage');
                }}
                getPageLink={getPageLink}
              />
            ) : undefined
          }
          errorContent={
            error && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                <Typography color="error.contrastText">{error}</Typography>
              </Box>
            )
          }
        />

        <Dialog open={poolDialogOpen} onClose={closePoolDialog} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Hub color="primary" />
            {editingPool ? t('pages:sipTrunks.pools.dialog.editTitle') : t('pages:sipTrunks.pools.dialog.createTitle')}
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label={t('pages:sipTrunks.pools.fields.name')}
              value={poolForm.name}
              onChange={(event) => setPoolForm((prev) => ({ ...prev, name: event.target.value }))}
              required
              fullWidth
              disabled={savingPool}
            />
            <TextField
              label={t('pages:sipTrunks.pools.fields.description')}
              value={poolForm.description}
              onChange={(event) => setPoolForm((prev) => ({ ...prev, description: event.target.value }))}
              fullWidth
              multiline
              minRows={2}
              disabled={savingPool}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label={t('pages:sipTrunks.pools.fields.maxConcurrentCalls')}
                value={poolForm.maxConcurrentCalls}
                onChange={(event) => setPoolForm((prev) => ({ ...prev, maxConcurrentCalls: event.target.value }))}
                type="number"
                inputProps={{ min: 1 }}
                helperText={t('pages:sipTrunks.pools.fields.maxConcurrentCallsHelper')}
                disabled={savingPool}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={poolForm.isActive}
                      onChange={(event) => setPoolForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                      disabled={savingPool}
                    />
                  )}
                  label={t('pages:sipTrunks.pools.fields.isActive')}
                />
                {isSuperuser && !editingPool && (
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={poolForm.isGlobal}
                        onChange={(event) => setPoolForm((prev) => ({ ...prev, isGlobal: event.target.checked }))}
                        disabled={savingPool}
                      />
                    )}
                    label={t('pages:sipTrunks.pools.fields.isGlobal')}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="subtitle2">{t('pages:sipTrunks.pools.membersTitle')}</Typography>
                <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={addPoolMember}
                disabled={savingPool || sipOutboundRouteOptions.length === 0}
              >
                {t('pages:sipTrunks.pools.actions.addMember')}
              </Button>
            </Box>

            {poolForm.members.length === 0 ? (
              <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('pages:sipTrunks.pools.noMembers')}
                </Typography>
              </Box>
            ) : (
              poolForm.members.map((member, index) => (
                <Box
                  key={member.id || index}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'minmax(220px, 1.5fr) 110px 130px 110px 90px 44px',
                    },
                    gap: 1.5,
                    alignItems: 'center',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <FormControl fullWidth size="small" required>
                    <InputLabel>{t('pages:sipTrunks.pools.fields.memberRoute')}</InputLabel>
                    <Select
                      value={member.sipTrunkOutboundRouteId}
                      label={t('pages:sipTrunks.pools.fields.memberRoute')}
                      onChange={(event) => {
                        const route = sipOutboundRouteOptions.find((option) => option.id === event.target.value);
                        updatePoolMember(index, {
                          sipTrunkOutboundRouteId: event.target.value,
                          sipTrunkOutboundRouteName: route?.name,
                          sipTrunkOutboundRouteIsActive: route?.isActive,
                          sipTrunkId: route?.sipTrunkId,
                          sipTrunkName: route?.sipTrunkName,
                          sipTrunkHost: route?.host,
                          sipTrunkPort: route?.port,
                        });
                      }}
                      disabled={savingPool}
                    >
                      {sipOutboundRouteOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {formatOutboundRouteOption(option)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label={t('pages:sipTrunks.pools.fields.baseWeight')}
                    value={member.baseWeight}
                    onChange={(event) => updatePoolMember(index, { baseWeight: Number(event.target.value) })}
                    type="number"
                    size="small"
                    inputProps={{ min: 0 }}
                    disabled={savingPool}
                  />
                  <TextField
                    label={t('pages:sipTrunks.pools.fields.memberMaxConcurrentCalls')}
                    value={member.maxConcurrentCalls ?? ''}
                    onChange={(event) => updatePoolMember(index, {
                      maxConcurrentCalls: event.target.value === '' ? null : Number(event.target.value),
                    })}
                    type="number"
                    size="small"
                    inputProps={{ min: 1 }}
                    disabled={savingPool}
                  />
                  <TextField
                    label={t('pages:sipTrunks.pools.fields.priority')}
                    value={member.priority}
                    onChange={(event) => updatePoolMember(index, { priority: Number(event.target.value) })}
                    type="number"
                    size="small"
                    inputProps={{ min: 0 }}
                    disabled={savingPool}
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={member.isActive}
                        onChange={(event) => updatePoolMember(index, { isActive: event.target.checked })}
                        disabled={savingPool}
                      />
                    )}
                    label={t('pages:sipTrunks.pools.fields.memberActive')}
                  />
                  <Tooltip title={t('common:actions.delete')}>
                    <IconButton
                      color="error"
                      onClick={() => removePoolMember(index)}
                      disabled={savingPool}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closePoolDialog} disabled={savingPool}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={savePool}
              disabled={savingPool || !isPoolFormValid}
            >
              {savingPool ? t('common:actions.saving') : t('common:actions.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {DialogComponent}
      </Box>
    </RequirePermission>
  );
}
